import {
  ArrivalReadingText, Badge, Button, Card, CheckInDialog, CheckOutDialog, ChildAvatar, ErrorState, Grid, Input,
  LoadingState, Notice, Row, Stat, Stack, useApi, useArrivalHealthCheck, useI18n,
  type AttendanceBoardRow,
} from '@daghfal/core';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The daily attendance board (FR-ATT-07).
 *
 * One row per child, one tap to check in, and a check-out that asks who is collecting. Where the nursery takes a
 * health check at arrival, the tap opens the reading first (FR-ATT-03), and the row shows it. The counters come
 * back with the rows rather than from a second request, so the figure at the top and the list under it are
 * the same computation and cannot disagree (FR-ATT-10).
 *
 * The board never decides who is absent on its own. That is the one absence rule, applied on the server,
 * because the dashboard and a child's history have to agree with this screen.
 */
export function Attendance() {
  const api = useApi();
  const { t, formatTime } = useI18n();
  const [search, setSearch] = useState('');
  const [collecting, setCollecting] = useState<AttendanceBoardRow | null>(null);
  const [arriving, setArriving] = useState<number | null>(null);
  const healthCheck = useArrivalHealthCheck();

  const board = useQuery({
    queryKey: ['attendance', 'board', search],
    queryFn: () => api.attendance.board(search ? { search } : {}),
    // The search box is part of this component, so a key change that emptied the tree would unmount the
    // input and take the caret with it — one character typed, then focus lost. The previous rows stay on
    // screen while the next ones load instead.
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['attendance'] });

  const checkIn = useMutation({
    mutationFn: (childId: number) => api.attendance.checkIn({ childId }),
    onSuccess: refresh,
  });

  if (board.isPending) return <LoadingState />;
  if (board.isError) return <ErrorState error={board.error} onRetry={() => void board.refetch()} />;

  const data = board.data;

  return (
    <Stack>
      <Grid>
        <Stat label={t('attendance.present')} value={data.present} tone="success" />
        <Stat label={t('attendance.absent')} value={data.absent} tone="danger" />
        <Stat label={t('attendance.notified')} value={data.notifiedAbsent} tone="warning" />
        <Stat label={t('attendance.expected')} value={data.expected} tone="neutral" />
      </Grid>

      {/* Two reasons the absent column may be zero that are worth saying out loud, rather than leaving a
          teacher to wonder whether the board is broken. */}
      {!data.operatingDay && <Notice tone="info">{t('attendance.closedDay')}</Notice>}
      {data.operatingDay && !data.cutoffPassed && (
        <Notice tone="info">{t('attendance.beforeCutoff')}</Notice>
      )}

      <Input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('action.search')}
        aria-label={t('action.search')}
      />

      {checkIn.isError && <ErrorState error={checkIn.error} />}

      <Stack>
        {data.rows.map((row) => (
          <Card key={row.childId}>
            <Row between>
              <span className="dg-row">
                {/* The allergies travel to the board too, on the photo: this is where a stranger collecting a child is
                    first identified, and where a handover conversation happens (FR-CHD-06, Q75, Q76). */}
                <ChildAvatar child={row} photoUrl={row.photoUrl} allergies={row.allergyMarks} />
                <span className="dg-stack dg-stack--tight">
                  <strong>{row.preferredName ?? `${row.firstName} ${row.lastName}`}</strong>
                  <span className="dg-muted dg-text-sm">{row.className}</span>
                  {row.checkInAt && (
                    <span className="dg-muted dg-text-sm">
                      {t('day.arrived', { time: formatTime(row.checkInAt) })}
                    </span>
                  )}
                  {row.checkOutAt && (
                    <span className="dg-muted dg-text-sm">
                      {t('day.left', { time: formatTime(row.checkOutAt) })}
                      {row.pickedUpByName
                        ? ` · ${t('attendance.collectedBy', { name: row.pickedUpByName })}`
                        : ''}
                    </span>
                  )}
                  <ArrivalReadingText row={row} />
                  {row.absenceReason && <span className="dg-muted dg-text-sm">{row.absenceReason}</span>}
                </span>
              </span>

              <span className="dg-row">
                <Badge tone={toneFor(row.status)}>{t(`attendance.${statusKey(row.status)}`)}</Badge>

                {row.status === 'Present' && (
                  <Button variant="secondary" onClick={() => setCollecting(row)}>
                    {t('attendance.checkOut')}
                  </Button>
                )}

                {(row.status === 'Expected' || row.status === 'Absent'
                  || row.status === 'NotifiedAbsence') && (
                  <Button
                    busy={checkIn.isPending && checkIn.variables === row.childId}
                    onClick={() => (healthCheck ? setArriving(row.childId) : checkIn.mutate(row.childId))}
                  >
                    {t('attendance.checkIn')}
                  </Button>
                )}
              </span>
            </Row>
            {arriving === row.childId && (
              <CheckInDialog
                row={row}
                onClose={() => setArriving(null)}
                onDone={() => { setArriving(null); void refresh(); }}
              />
            )}
          </Card>
        ))}
      </Stack>

      {collecting && (
        <CheckOutDialog
          row={collecting}
          onClose={() => setCollecting(null)}
          onDone={() => { setCollecting(null); void refresh(); }}
        />
      )}
    </Stack>
  );

  function statusKey(status: AttendanceBoardRow['status']): string {
    switch (status) {
      case 'Present': return 'present';
      case 'Departed': return 'departed';
      case 'Absent': return 'absent';
      case 'NotifiedAbsence': return 'notified';
      default: return 'expected';
    }
  }

  function toneFor(status: AttendanceBoardRow['status']) {
    switch (status) {
      case 'Present': return 'success' as const;
      case 'Departed': return 'info' as const;
      case 'Absent': return 'danger' as const;
      case 'NotifiedAbsence': return 'warning' as const;
      default: return 'neutral' as const;
    }
  }
}
